import React from 'react';
import i18n from 'i18n-js';
import { MomentInput } from 'moment';

import { Popup, localTime, isPhone } from '@helpers';
import { font } from '@styles/fonts';
import Button from '@styles/buttons';

import Calendar from '@components/common/Calendar';
import HeaderArrow from './HeaderArrow';
import HeaderZoom from './HeaderZoom';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { useManageDateHeader, ACTION_NAME, DATE_HEADER_HEIGHT, UseManageDateHeaderParams } from './hooks/useManageDateHeader';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface DateHeaderProps extends UseManageDateHeaderParams {
  isZoomInDisabled: boolean;
  isZoomResetDisabled: boolean;
  withoutSelectedPeriodButton?: boolean;
}

const DateHeader = ({
  C,
  isZoomInDisabled,
  isZoomResetDisabled,
  withoutSelectedPeriodButton,
  ...hookParams
}: DateHeaderProps) => {
  const {
    selectedTime,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleLeftArrowPress,
    handleRightArrowPress,
    handleSelectCurrentPeriod,
    setDate,
    selectCustomRange,
    computedIsZoomOutDisabled,
    computedRightArrowDisabled,
    dateLabel,
  } = useManageDateHeader({ C, ...hookParams });

  const showCalendar = () => {
    Popup({
      Component: (p: any) => (
        <Calendar
          {...{
            ...p,
            C,
            setSelectedChartDate: setDate,
            selectRange: selectCustomRange,
            selectedChartDate: localTime(hookParams.startTime).format(),
            mode: 'range',
          }}
        />
      ),
      avoidingViewEnable: false,
    });
  };

  return (
    <div
      style={{
        width: '100%',
        height: DATE_HEADER_HEIGHT,
        justifyContent: 'center',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: C('Background/Light/Field'),
        position: 'relative',
        borderBottomColor: C('Hairline/Light/No trans'),
        borderBottomWidth: 1,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
      }}
    >
      <div data-tour-id={WELCOME_TOUR_IDS.TIMELINE_SCALE} style={{ position: 'absolute', left: 13, top: 7, zIndex: 1000, display: 'flex' }}>
        {!isPhone && (
          <HeaderZoom
            {...{
              C,
              zoomIcon: 'zoomOutGlass',
              onClick: handleZoomOut,
              disabled: computedIsZoomOutDisabled,
              testID: TEST_IDS.dateHeader.zoomOut,
            }}
          />
        )}
        <div style={{ marginLeft: 18 }}>
          <HeaderZoom
            {...{
              C,
              zoomIcon: 'zoomInGlass',
              onClick: handleZoomIn,
              disabled: isZoomInDisabled,
              testID: TEST_IDS.dateHeader.zoomIn,
            }}
          />
        </div>
        <div style={{ marginLeft: 18 }}>
          <HeaderZoom
            {...{
              C,
              zoomIcon: 'zoomResetGlass',
              onClick: handleZoomReset,
              disabled: isZoomResetDisabled,
              testID: TEST_IDS.dateHeader.zoomReset,
            }}
          />
        </div>
      </div>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          height: DATE_HEADER_HEIGHT,
          width: '100%',
          userSelect: 'none',
        }}
      >
        <div>
          <div style={{ height: DATE_HEADER_HEIGHT, justifyContent: 'center', alignItems: 'center', flexDirection: 'row', display: 'flex' }}>
            <Button {...{ variant: 'M', type: 'Only Text Link', onPress: showCalendar }}>
              <div style={{ ...font.Body, lineHeight: '22px', color: C('Button/Light/Primary'), textAlign: 'center', fontVariant: ['tabular-nums'] }}>
                {dateLabel}
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', zIndex: 1000, right: 13, bottom: 10, display: 'flex', flexDirection: 'row' }}>
        {!withoutSelectedPeriodButton && (
          <div style={{ marginRight: 18 }}>
            <Button
              {...{
                variant: 'M',
                type: 'Secondary',
                onPress: handleSelectCurrentPeriod,
                testID: TEST_IDS.dateHeader.todayButton,
              }}
            >
              {i18n.t(ACTION_NAME[selectedTime])}
            </Button>
          </div>
        )}
        <div style={{ transform: 'rotate(180deg)', marginRight: 18 }}>
          <HeaderArrow
            {...{
              C,
              disabled: hookParams.leftArrowDisabled,
              onClick: handleLeftArrowPress,
              testID: TEST_IDS.dateHeader.leftArrow,
            }}
          />
        </div>
        <HeaderArrow
          {...{
            C,
            disabled: computedRightArrowDisabled,
            onClick: handleRightArrowPress,
            testID: TEST_IDS.dateHeader.rightArrow,
          }}
        />
      </div>
    </div>
  );
};

export default DateHeader;
